// TransactionsAudit.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Grid,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TableContainer,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { motion } from "framer-motion";
import Swal from "sweetalert2";
import jsPDF from "jspdf";
import "jspdf-autotable";
import savvyLogo from "../assets/img/savvy-transparent-logo.png";
import { fetchBillableStatement, fetchTransactionAudit } from "../services/transactionAuditService";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { useSearchParams } from "react-router-dom";

// ---- THEME
const brand = "#800b33";
const brandHover = "#6c0a2b";
const actionButtonBase = {
  whiteSpace: "nowrap",
  textTransform: "none",
  fontWeight: 700,
  borderRadius: 2,
  px: 2.5,
  minHeight: 42,
  flexShrink: 0,
};
const actionButtonPrimary = {
  ...actionButtonBase,
  bgcolor: brand,
  color: "#fff",
  "&:hover": { bgcolor: brandHover },
};
const actionButtonOutlined = {
  ...actionButtonBase,
  borderColor: brand,
  color: brand,
  "&:hover": { borderColor: brandHover, bgcolor: "rgba(128,11,51,0.06)" },
};

// ---- UTILITIES
const tzs = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = typeof value === "string" ? Number(value) : value;
  if (typeof num === "number" && !Number.isNaN(num)) {
    return new Intl.NumberFormat("en-TZ", { maximumFractionDigits: 0 }).format(num);
  }
  return String(value);
};

const fmtDate = (d) => {
  if (!d) return "-";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toISOString().slice(0, 10);
};

const fmtDateTime24 = (d) => {
  if (!d) return "-";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return new Intl.DateTimeFormat("en-GB", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
    .format(dt)
    .replace(",", "");
};

const hasStatementBalance = (value) => value !== null && value !== undefined && value !== "";
const toEndOfDay = (value) => {
  if (!value) return null;
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return null;
  dt.setHours(23, 59, 59, 999);
  return dt;
};
const LEDGER_DEBIT_TYPES = new Set([
  "WITHDRAWAL",
  "PENALTY",
  "EMERGENCY_DEDUCTION",
  "LOAN_DISBURSEMENT",
  "UPATU_PAYOUT",
  "STATEMENT_FEE",
  "EXPENSE_SHARE",
  "ENTRY_FEE",
  "ADJUSTMENT",
]);
const DISCLOSURE_ONLY_TYPES = new Set([
  "SYSTEM_REGISTRATION_FEE",
  "SYSTEM_ANNUAL_FEE",
]);

// CSV export for any rows/columns
const exportCSV = (filename, rows, columns) => {
  const header = columns.map((c) => `"${c.header}"`).join(",");
  const body = rows
    .map((r) =>
      columns
        .map((c) => {
          const v = typeof c.accessor === "function" ? c.accessor(r) : r[c.accessor];
          return `"${String(v ?? "").replace(/"/g, '""')}"`;
        })
        .join(",")
    )
    .join("\n");
  const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const generateStatementPDF = (member, period, summary, transactions, groupName, options = {}) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const platformName = "Savvy Community";
  const logoUrl = savvyLogo;
  let rendered = false;

  const render = (logo, options = {}) => {
    if (rendered) return;
    rendered = true;

    const {
      retrievedBy,
      retrievedAt,
      generatedAt,
      groupStartDate,
      groupEndDate,
      groupCycleType,
    } = options || {};

    const groupEndDisplay =
      String(groupCycleType || "").toUpperCase() === "PERMANENT"
        ? "Ongoing"
        : groupEndDate
          ? fmtDate(groupEndDate)
          : "Ongoing";

    const topMargin = 72;

    // Tables render first; we'll draw headers (with page numbers) across every page after.
    doc.autoTable({
      startY: topMargin,
      margin: { top: topMargin },
      body: [
        [`Opening Balance:`, `${tzs(summary.opening)} TZS`],
        [`Total Deposits/Contributions:`, `${tzs(summary.deposits)} TZS`],
        [`Total Withdrawals/Deductions:`, `${tzs(summary.withdrawals)} TZS`],
        [`Total Fees & Charges (Disclosed):`, `${tzs(summary.disclosedFees)} TZS`],
        [`Closing Balance:`, `${tzs(summary.closing)} TZS`],
      ],
      theme: "grid",
      styles: { fontSize: 10, cellPadding: 2 },
      columnStyles: {
        0: { fontStyle: "bold" },
        1: { halign: "right" },
      },
    });

    // 4. Transactions Table
    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 10,
      margin: { top: topMargin },
      head: [["Date", "Description", "Debit (-)", "Credit (+)", "Balance"]],
      body: transactions.map((tx) => {
        const rawAmount = Number(tx.amount) || 0;
        const isDebit = rawAmount < 0 || LEDGER_DEBIT_TYPES.has(tx.transaction_type) || DISCLOSURE_ONLY_TYPES.has(tx.transaction_type);
        const amount = tzs(Math.abs(rawAmount));
        return [
          fmtDate(tx.created_at),
          tx.description || tx.transaction_type_display,
          isDebit ? amount : "",
          !isDebit ? amount : "",
          tzs(tx.balance_after_transaction),
        ];
      }),
      theme: "striped",
      headStyles: { fillColor: [128, 11, 51], textColor: 255, fontStyle: "bold" },
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);

      if (page === 1) {
        if (logo) doc.addImage(logo, "PNG", 14, 10, 20, 20);
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text(platformName, pageWidth / 2, 18, { align: "center" });
        doc.setFontSize(12);
        doc.text(groupName || "Savvy Community Group", pageWidth / 2, 26, { align: "center" });
        doc.setFontSize(11);
        doc.setFont("helvetica", "normal");
        doc.text("Member Financial Statement", pageWidth / 2, 32, { align: "center" });
      } else {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.text(platformName, 14, 14);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.text(groupName || "Savvy Community Group", 14, 20);
      }

      // Left: member + group period (appears on every page)
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(`Member: ${member.name}`, 14, 44);
      doc.text(`Payment Reference Number: ${member.paymentReference || member.id}`, 14, 49);
      doc.text(`Group Start Date: ${groupStartDate ? fmtDate(groupStartDate) : "-"}`, 14, 54);
      doc.text(`Group End Date: ${groupEndDisplay}`, 14, 59);

      // Right: generated/retrieved metadata + page number
      const rightX = pageWidth - 14;
      doc.text(`Statement Period: ${period.from} - ${period.to}`, rightX, 44, { align: "right" });
      doc.text(`Retrieved Date: ${retrievedAt ? fmtDateTime24(retrievedAt) : fmtDateTime24(new Date())}`, rightX, 49, { align: "right" });
      doc.text(`Retrieved By: ${retrievedBy || "-"}`, rightX, 54, { align: "right" });
      doc.text(`Page ${page}.`, rightX, 59, { align: "right" });
    }

    doc.save(`Member_Statement_${member.id}_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => render(img, options);
  img.onerror = (err) => {
    console.error("Failed to load logo for PDF:", err);
    render(null, options);
  };
  img.src = logoUrl;
};

const TRANSACTION_TYPES = [
  { value: "CONTRIBUTION", labelKey: "Contribution" },
  { value: "PENALTY", labelKey: "Penalty" },
  { value: "LOAN_DISBURSEMENT", labelKey: "Loan Disbursement" },
  { value: "LOAN_REPAYMENT", labelKey: "Loan Repayment" },
  { value: "WITHDRAWAL", labelKey: "Withdrawal" },
  { value: "ADJUSTMENT", labelKey: "Adjustment" },
  { value: "EMERGENCY_DEDUCTION", labelKey: "Emergency Deduction" },
  { value: "BUSINESS_INCOME", labelKey: "Business Income" },
  { value: "DIVIDEND_PAYOUT", labelKey: "Dividend Payout" },
  { value: "DIVIDEND_BANK_CHARGE", labelKey: "Dividend Bank Charge" },
  { value: "LOAN_INTEREST_INCOME", labelKey: "Loan Interest Income" },
  { value: "DEFAULTS_COLLECTED", labelKey: "Defaults Collected" },
  { value: "ENTRY_FEE", labelKey: "Entry Fee" },
  { value: "STATEMENT_FEE", labelKey: "Statement Fee" },
  { value: "SYSTEM_REGISTRATION_FEE", labelKey: "System Registration Fee" },
  { value: "SYSTEM_ANNUAL_FEE", labelKey: "System Annual Fee" },
  { value: "UPATU_PAYOUT", labelKey: "Upatu Payout" },
];

// ---- ANIMATIONS
const fadeIn = {
  hidden: { opacity: 0, y: 18 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.35, ease: "easeOut" },
  }),
};

const statementChipColor = (type) => {
  switch (type) {
    case "CONTRIBUTION":
      return "primary";
    case "WITHDRAWAL":
      return "warning";
    case "PENALTY":
      return "error";
    case "DIVIDEND_PAYOUT":
    case "BUSINESS_INCOME":
      return "success";
    case "EMERGENCY_DEDUCTION":
      return "secondary";
    case "SYSTEM_REGISTRATION_FEE":
    case "SYSTEM_ANNUAL_FEE":
      return "warning";
    default:
      return "default";
  }
};

// ---- TABLE COLUMNS DEFINITIONS
const getStatementColumns = (t) => [
  { header: t("Date"), accessor: (r) => fmtDate(r.created_at) },
  { header: t("Member"), accessor: "member_name" },
  {
    header: t("Type"),
    accessor: (r) => r.transaction_type_display || r.transaction_type,
  },
  {
    header: t("Amount (TZS)"),
    accessor: (r) => tzs(r.amount),
  },
  {
    header: t("Overpayment (TZS)"),
    accessor: (r) => tzs(r.overpayment_amount),
  },
  {
    header: t("Balance After (TZS)"),
    accessor: (r) => tzs(r.balance_after_transaction),
  },
  { header: t("Description"), accessor: "description" },
];

const getAuditColumns = (t) => [
  {
    header: t("Created"),
    accessor: (r) => new Date(r.created_at).toLocaleString(),
  },
  { header: t("Event"), accessor: "event_name" },
  {
    header: t("Event Type"),
    accessor: (r) => r.event_type_display || r.event_type,
  },
  {
    header: t("Status"),
    accessor: (r) => r.status_display || r.status,
  },
  {
    header: t("Amount Per Member (TZS)"),
    accessor: (r) => tzs(r.amount_per_member),
  },
  { header: t("Initiated By"), accessor: "initiated_by_name" },
];

const adaptLegacyRecords = (payload = {}) => {
  const fallback = [];

  (payload.statements || []).forEach((tx) => {
    fallback.push({
      timestamp: tx.created_at,
      module: "contributions",
      action: tx.transaction_type_display || tx.transaction_type,
      amount: tx.amount,
      actor: tx.member_name,
      reference: `TX-${tx.id}`,
      metadata: {
        member_id: tx.member_id,
        transaction_type: tx.transaction_type,
        overpayment_amount: tx.overpayment_amount,
        balance_after_transaction: tx.balance_after_transaction,
        description: tx.description,
      },
    });
  });

  (payload.program_logs || []).forEach((log) => {
    fallback.push({
      timestamp: log.created_at,
      module: "emergency_contributions",
      action: `${log.event_name} (${log.status_display || log.status})`,
      amount: log.amount_per_member,
      actor: log.initiated_by_name,
      reference: `EM-${log.id}`,
      metadata: {
        event_name: log.event_name,
        status: log.status,
        status_display: log.status_display,
        event_type: log.event_type,
        event_type_display: log.event_type_display,
      },
    });
  });

  return fallback;
};
// ---- MAIN PAGE
export default function TransactionsAudit() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const tabParam = searchParams.get("tab");
  const initialTab = (() => {
    if (!tabParam) return 0;
    const parsed = Number(tabParam);
    return Number.isNaN(parsed) ? 0 : parsed;
  })();
  const [tab, setTab] = useState(initialTab);

  // shared filters
  const [query, setQuery] = useState(() => searchParams.get("query") || "");
  const [memberId, setMemberId] = useState(() => searchParams.get("memberId") || "all");
  const [typeFilter, setTypeFilter] = useState(() => searchParams.get("typeFilter") || "all");
  const [from, setFrom] = useState(() => searchParams.get("from") || "");
  const [to, setTo] = useState(() => searchParams.get("to") || "");

  // pagination per tab
  const [pageStatement, setPageStatement] = useState(0);
  const [rppStatement, setRppStatement] = useState(10);
  const [pageAudit, setPageAudit] = useState(0);
  const [rppAudit, setRppAudit] = useState(10);

  // Term Logic
  const [terms, setTerms] = useState([]);
  const [termId, setTermId] = useState(() => searchParams.get("termId") || "all");
  const termInitializedRef = useRef(Boolean(searchParams.get("termId")));

  useEffect(() => {
    let active = true;
    import("../services/groupService").then(({ fetchGroupTerms }) => {
      fetchGroupTerms().then(data => {
        if (active) {
          // If we have terms, maybe default to active? Or "All"?
          // Let's set terms list.
          // data might be pagination result or list. Check ViewSet. 
          // ModelViewSet usually returns list or {count, results}.
          // ViewSet ReadOnlyModelViewSet defaults to pagination unless turned off.
          // Assuming standard pagination result: { results: [...] } or just [...] if pagination off.
          // But let's handle array.
          const list = Array.isArray(data) ? data : (data.results || []);
          setTerms(list);
        }
      }).catch(err => console.error("Failed to fetch terms", err));
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (termInitializedRef.current) return;
    if (!terms.length) return;
    const activeTerm = terms.find((term) => term.status === "ACTIVE");
    if (activeTerm?.id) {
      setTermId(String(activeTerm.id));
      termInitializedRef.current = true;
    }
  }, [terms]);

  // data state
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [billingLoading, setBillingLoading] = useState(false);
  const [statementInvoice, setStatementInvoice] = useState(null);
  const [statementUnlocked, setStatementUnlocked] = useState(false);
  const [statementMeta, setStatementMeta] = useState(null);
  const [statementGeneratedOn, setStatementGeneratedOn] = useState(null);


  const resetFilters = () => {
    setQuery("");
    setMemberId("all");
    setTypeFilter("all");
    setTermId("all");
    setFrom("");
    setTo("");
    setPageStatement(0);
    setPageAudit(0);
  };

  const contributionRows = useMemo(() => {
    return records
      .filter((rec) => rec.module === "contributions" || rec.module === "system_fees")
      .map((rec) => ({
        id: rec.reference || rec.metadata?.transaction_id || rec.timestamp,
        created_at: rec.timestamp,
        member_name: rec.actor || "-",
        member_id: rec.metadata?.member_id,
        transaction_type: rec.metadata?.transaction_type,
        transaction_type_display: rec.action,
        amount: rec.amount,
        overpayment_amount: rec.metadata?.overpayment_amount,
        balance_after_transaction: rec.metadata?.balance_after_transaction,
        description: rec.metadata?.description,
        statement_section: rec.metadata?.statement_section || (rec.module === "system_fees" ? "SYSTEM_FEES" : "MEMBER_LEDGER"),
      }));
  }, [records]);

  const programRows = useMemo(() => {
    return records
      .filter((rec) => rec.module === "emergency_contributions")
      .map((rec) => ({
        id: rec.reference || rec.timestamp,
        created_at: rec.timestamp,
        event_name: rec.metadata?.event_name || rec.action,
        event_type: rec.metadata?.event_type,
        event_type_display: rec.metadata?.event_type_display,
        status: rec.metadata?.status,
        status_display: rec.metadata?.status_display,
        amount_per_member: rec.amount,
        initiated_by_name: rec.actor || "-",
      }));
  }, [records]);

  const apiFilters = useMemo(() => {
    const params = {};
    if (memberId !== "all") params.member_id = memberId;
    if (typeFilter !== "all") params.transaction_type = typeFilter;
    if (termId !== "all" && termId !== "") params.term_id = termId;
    if (from) params.start_date = from;
    if (to) params.end_date = to;
    return params;
  }, [memberId, typeFilter, termId, from, to]);

  const fallbackErrorMessage = t("Failed to load transaction audit data.");

  useEffect(() => {
    let active = true;
    const loadData = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchTransactionAudit(apiFilters);
        if (!active) return;
        if (Object.prototype.hasOwnProperty.call(data || {}, "records")) {
          setRecords(data.records || []);
        } else {
          setRecords(adaptLegacyRecords(data));
        }
      } catch (err) {
        if (!active) return;
        setError(err.message || fallbackErrorMessage);
        setRecords([]);
      } finally {
        if (active) setLoading(false);
      }
    };

    loadData();
    return () => {
      active = false;
    };
  }, [apiFilters, refreshKey, fallbackErrorMessage]);

  const handleManualRefresh = () => setRefreshKey((k) => k + 1);
  const handleBillableDownload = async () => {
    setBillingLoading(true);
    try {
      const data = await fetchBillableStatement(apiFilters);
      setStatementInvoice(null);
      setStatementUnlocked(true);
      setStatementMeta({
        group_name: data?.group_name || user?.group_name,
        group_start_date: data?.group_start_date || null,
        group_end_date: data?.group_end_date || null,
        group_cycle_type: data?.group_cycle_type || null,
      });
      setStatementGeneratedOn(data?.generated_on || new Date().toISOString());
      if (Object.prototype.hasOwnProperty.call(data || {}, "records")) {
        setRecords(data.records || []);
      } else {
        setRecords(adaptLegacyRecords(data));
      }
      Swal.fire({
        icon: "success",
        title: t("Statement ready"),
        text: t("Invoice settled. You can view/export the refreshed statement below."),
        confirmButtonColor: brand,
      });
    } catch (err) {
      if (err.status === 402 && err.invoice) {
        setStatementInvoice(err.invoice);
        setStatementUnlocked(false);
      } else {
        setError(err.message || fallbackErrorMessage);
      }
    } finally {
      setBillingLoading(false);
    }
  };

  const handleDownloadPDF = () => {
    if (!statementUnlocked) {
      Swal.fire({
        icon: "info",
        title: t("Complete payment"),
        text: t("Generate and settle your statement invoice before downloading."),
        confirmButtonColor: brand,
      });
      return;
    }
    if (statementFiltered.length === 0) {
      Swal.fire({ icon: "warning", title: t("No Data"), text: t("There are no transactions to generate a statement for.") });
      return;
    }

    const member = { id: user.member_id, name: user.full_name, paymentReference: user.payment_reference };
    // System fees are disclosed in the unified statement, but they do not mutate
    // the member savings ledger balance. Use balance-bearing rows only for
    // opening/closing balance math.
    const balanceRows = statementFiltered.filter((row) => hasStatementBalance(row.balance_after_transaction));
    const disclosedFeeRows = statementFiltered.filter(
      (row) => !hasStatementBalance(row.balance_after_transaction) && DISCLOSURE_ONLY_TYPES.has(row.transaction_type)
    );
    const opening = balanceRows.length > 0
      ? (Number(balanceRows[balanceRows.length - 1]?.balance_after_transaction) || 0)
        - (Number(balanceRows[balanceRows.length - 1]?.amount) || 0)
      : 0;
    const closing = balanceRows.length > 0
      ? (Number(balanceRows[0]?.balance_after_transaction) || 0)
      : 0;
    const deposits = balanceRows.reduce((sum, row) => {
      const amount = Number(row.amount) || 0;
      const isDebit = amount < 0 || LEDGER_DEBIT_TYPES.has(row.transaction_type);
      return isDebit ? sum : sum + Math.abs(amount);
    }, 0);
    const withdrawals = balanceRows.reduce((sum, row) => {
      const amount = Number(row.amount) || 0;
      const isDebit = amount < 0 || LEDGER_DEBIT_TYPES.has(row.transaction_type);
      return isDebit ? sum + Math.abs(amount) : sum;
    }, 0);
    const disclosedFees = disclosedFeeRows.reduce((sum, row) => sum + Math.abs(Number(row.amount) || 0), 0);

    const groupName = statementMeta?.group_name || user?.group_name || "Savvy Community Group";
    generateStatementPDF(
      member,
      { from: from || "Start", to: to || "Today" },
      { opening, deposits, withdrawals, disclosedFees, closing },
      statementFiltered,
      groupName,
      {
        generatedAt: statementGeneratedOn,
        retrievedAt: new Date().toISOString(),
        retrievedBy: user?.full_name,
        groupStartDate: statementMeta?.group_start_date,
        groupEndDate: statementMeta?.group_end_date,
        groupCycleType: statementMeta?.group_cycle_type,
      }
    );
  };

  const memberOptions = useMemo(() => {
    const seen = new Map();
    contributionRows.forEach((s) => {
      if (s.member_id && !seen.has(String(s.member_id))) {
        seen.set(String(s.member_id), s.member_name || `Member ${s.member_id}`);
      }
    });
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [contributionRows]);

  // ---- FILTERED DATA (STATEMENT)
  const statementFiltered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const fromDate = from ? new Date(from) : null;
    const toDate = toEndOfDay(to);

    return contributionRows.filter((r) => {
      if (memberId !== "all" && String(r.member_id) !== String(memberId)) return false;
      if (typeFilter !== "all" && r.transaction_type !== typeFilter) return false;

      const created = r.created_at ? new Date(r.created_at) : null;
      if (fromDate && created && created < fromDate) return false;
      if (toDate && created && created > toDate) return false;

      if (q) {
        const hay = `${r.member_name ?? ""} ${r.transaction_type_display ?? ""} ${r.description ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [contributionRows, query, memberId, typeFilter, from, to]);

  const statementPaginated = useMemo(() => {
    if (rppStatement === -1) return statementFiltered;
    const start = pageStatement * rppStatement;
    return statementFiltered.slice(start, start + rppStatement);
  }, [statementFiltered, pageStatement, rppStatement]);

  // ---- FILTERED DATA (AUDIT)
  const auditFiltered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const fromDate = from ? new Date(from) : null;
    const toDate = toEndOfDay(to);

    return programRows.filter((r) => {
      const created = r.created_at ? new Date(r.created_at) : null;
      if (fromDate && created && created < fromDate) return false;
      if (toDate && created && created > toDate) return false;

      if (q) {
        const hay = `${r.event_name ?? ""} ${r.status_display ?? ""} ${r.initiated_by_name ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [programRows, query, from, to]);

  const auditPaginated = useMemo(() => {
    if (rppAudit === -1) return auditFiltered;
    const start = pageAudit * rppAudit;
    return auditFiltered.slice(start, start + rppAudit);
  }, [auditFiltered, pageAudit, rppAudit]);

  // ---- HANDLERS
  const statementColumns = useMemo(() => getStatementColumns(t), [t]);
  const auditColumns = useMemo(() => getAuditColumns(t), [t]);

  // ---- RENDER
  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 900, color: brand }}>
          {t("Transactions & Audit")}
        </Typography>
        <Typography variant="subtitle2" color="text.secondary">
          {t("Unified financial view — Member Statements & Program Audit Logs")}
        </Typography>
      </Box>

      {statementInvoice && (
        <Box sx={{ mb: 2 }}>
          <Alert
            severity="info"
            action={
              <Button color="inherit" size="small" onClick={() => setStatementInvoice(null)}>
                {t("Dismiss")}
              </Button>
            }
          >
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {t("Payment required before download")}
              </Typography>
              <Typography variant="body2">
                {t("Pages")}: {statementInvoice.pages || "N/A"} • {t("Amount")}: {statementInvoice.total_amount || statementInvoice.amount} •{" "}
                {t("Pay with reference")}: <strong>{statementInvoice.payment_reference || t("Your member reference")}</strong>
              </Typography>
            </Box>
          </Alert>
        </Box>
      )}

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={handleManualRefresh}>
                {t("Retry")}
              </Button>
            }
          >
            {error}
          </Alert>
        </Box>
      )}

      {/* Tabs */}
      <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 2 }}>
        <CardContent sx={{ pb: 0 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            textColor="inherit"
            TabIndicatorProps={{ style: { background: brand } }}
          >
            <Tab label={t("Member Statement")} sx={{ fontWeight: 700 }} />
            <Tab label={t("Program Audit Log")} sx={{ fontWeight: 700 }} />
          </Tabs>
        </CardContent>

        {/* Filters */}
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder={t("Search text (member, notes, action)")}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPageStatement(0);
                  setPageAudit(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Member + Type are meaningful on Statement tab */}
            {tab === 0 && (
              <>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>{t("Member")}</InputLabel>
                    <Select
                      label={t("Member")}
                      value={memberId}
                      onChange={(e) => {
                        setMemberId(e.target.value);
                        setPageStatement(0);
                      }}
                    >
                      <MenuItem value="all">{t("All")}</MenuItem>
                      {memberOptions.map((m) => (
                        <MenuItem key={m.id} value={m.id}>
                          {m.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>{t("Type")}</InputLabel>
                    <Select
                      label={t("Type")}
                      value={typeFilter}
                      onChange={(e) => {
                        setTypeFilter(e.target.value);
                        setPageStatement(0);
                      }}
                    >
                      <MenuItem value="all">{t("All")}</MenuItem>
                      {TRANSACTION_TYPES.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {t(type.labelKey)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            {/* Date range works on both tabs */}
            <Grid item xs={6} md={tab === 0 ? 1.5 : 2}>
              <FormControl fullWidth>
                <InputLabel>{t("Term")}</InputLabel>
                <Select
                  label={t("Term")}
                  value={termId}
                  onChange={(e) => {
                    setTermId(e.target.value);
                    setPageStatement(0);
                    setPageAudit(0);
                  }}
                >
                  <MenuItem value="all">{t("All Terms")}</MenuItem>
                  {terms.map((term) => (
                    <MenuItem key={term.id} value={term.id}>
                      {term.name} ({term.status_display})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6} md={tab === 0 ? 1.5 : 2}>
              <TextField
                label={t("From")}
                type="date"
                value={from}
                onChange={(e) => {
                  setFrom(e.target.value);
                  setPageStatement(0);
                  setPageAudit(0);
                }}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} md={tab === 0 ? 1.5 : 2}>
              <TextField
                label={t("To")}
                type="date"
                value={to}
                onChange={(e) => {
                  setTo(e.target.value);
                  setPageStatement(0);
                  setPageAudit(0);
                }}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Actions */}
            <Grid
              item
              xs={12}
              md={tab === 0 ? 3 : 4}
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-end", md: "flex-end" },
                gap: 1.25,
                flexWrap: "nowrap",
                overflowX: { xs: "auto", md: "visible" },
              }}
            >
              <Tooltip title={t("Reset filters")}>
                <IconButton onClick={resetFilters} sx={{ flexShrink: 0 }}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="outlined"
                startIcon={<AutorenewIcon />}
                onClick={handleManualRefresh}
                sx={actionButtonOutlined}
              >
                {t("Refresh Data")}
              </Button>
              {tab === 0 && !statementInvoice && (
                <Button
                  variant="contained"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleBillableDownload}
                  disabled={billingLoading}
                  sx={actionButtonPrimary}
                >
                  {billingLoading ? t("Checking...") : t("Generate Statement")}
                </Button>
              )}
              {tab === 0 && statementInvoice && (
                <Button variant="contained" color="warning" sx={actionButtonBase} onClick={handleBillableDownload} disabled={billingLoading}>
                  {t("Payment Pending")}
                </Button>
              )}
              {tab === 0 && statementFiltered.length > 0 && statementUnlocked && !statementInvoice && (
                <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleDownloadPDF} sx={actionButtonOutlined}>
                  {t("Download PDF")}
                </Button>
              )}

              {/* Original Export Logic - can be removed or kept as an alternative */}
              {/* {tab === 0 ? (
                <Button
                  variant="contained"
                  startIcon={<FileDownloadIcon />}
                  onClick={onExportStatement}
                  sx={actionButtonPrimary}
                >
                  {t("Export CSV (Statement)")}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<FileDownloadIcon />}
                  onClick={onExportAudit}
                  sx={actionButtonPrimary}
                >
                  {t("Export CSV (Audit)")}
                </Button>
              )} */}
            </Grid>
          </Grid>
        </CardContent>
        {loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 3, pb: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              {t("Loading latest records...")}
            </Typography>
          </Box>
        )}
      </Card>

      {/* TAB CONTENT */}
      {tab === 0 ? (
        <motion.div variants={fadeIn} initial="hidden" animate="visible">
          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
            <CardContent sx={{ p: 0 }}>
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t("Date")}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t("Member")}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t("Type")}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        {t("Amount (TZS)")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        {t("Overpayment (TZS)")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        {t("Balance After (TZS)")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t("Description")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : statementPaginated.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 6, color: "text.secondary" }}>
                          {t("No transactions match your filters.")}
                        </TableCell>
                      </TableRow>
                    ) : (
                      statementPaginated.map((r, idx) => (
                        <TableRow key={r.id} hover>
                          <TableCell>
                            {rppStatement === -1
                              ? idx + 1
                              : pageStatement * rppStatement + idx + 1}
                          </TableCell>
                          <TableCell>{fmtDate(r.created_at)}</TableCell>
                          <TableCell>{r.member_name}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={r.transaction_type_display || r.transaction_type}
                              color={statementChipColor(r.transaction_type)}
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell align="right">{tzs(r.amount)}</TableCell>
                          <TableCell align="right">{tzs(r.overpayment_amount)}</TableCell>
                          <TableCell align="right">{tzs(r.balance_after_transaction)}</TableCell>
                          <TableCell>{r.description || "-"}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider />
              <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2 }}>
                <TablePagination
                  component="div"
                  count={statementFiltered.length}
                  page={pageStatement}
                  onPageChange={(_, p) => setPageStatement(p)}
                  rowsPerPage={
                    rppStatement === -1 ? statementFiltered.length || 0 : rppStatement
                  }
                  onRowsPerPageChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    setRppStatement(v);
                    setPageStatement(0);
                  }}

                  rowsPerPageOptions={[10, 25, 50, 100, { label: t("All"), value: -1 }]}
                />
              </Box>
            </CardContent>
          </Card>
        </motion.div>
      ) : (
        <motion.div variants={fadeIn} initial="hidden" animate="visible">
          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
            <CardContent sx={{ p: 0 }}>
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t("Created")}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t("Event")}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t("Event Type")}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t("Status")}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        {t("Amount Per Member (TZS)")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t("Initiated By")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : auditPaginated.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 6, color: "text.secondary" }}>
                          {t("No audit entries match your filters.")}
                        </TableCell>
                      </TableRow>
                    ) : (
                      auditPaginated.map((r, idx) => (
                        <TableRow key={r.id} hover>
                          <TableCell>
                            {rppAudit === -1 ? idx + 1 : pageAudit * rppAudit + idx + 1}
                          </TableCell>
                          <TableCell>{new Date(r.created_at).toLocaleString()}</TableCell>
                          <TableCell>{r.event_name}</TableCell>
                          <TableCell>{r.event_type_display || r.event_type}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={r.status_display || r.status}
                              color={r.status === "REJECTED" ? "error" : "default"}
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell align="right">{tzs(r.amount_per_member)}</TableCell>
                          <TableCell>{r.initiated_by_name || "-"}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider />
              <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2 }}>
                <TablePagination
                  component="div"
                  count={auditFiltered.length}
                  page={pageAudit}
                  onPageChange={(_, p) => setPageAudit(p)}
                  rowsPerPage={rppAudit === -1 ? auditFiltered.length || 0 : rppAudit}
                  onRowsPerPageChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    setRppAudit(v);
                    setPageAudit(0);
                  }}

                  rowsPerPageOptions={[10, 25, 50, 100, { label: t("All"), value: -1 }]}
                />
              </Box>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </Box>
  );
}
